import { useState, useContext } from "react";
import { Form } from "../../../../layout/FormLayout";
import { Controller } from "react-hook-form";
import {
  useStoreState,
  convertUserToOption,
  useFormWrapper,
} from "../../../../util/util";
import type {
  OptionType,
  Tenant,
  User,
  ITenantCustomerSettingsCreation,
} from "../../../../types/types";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import Axios from "axios";
import { endpoints } from "../../../../endpoints";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormContainer } from "./CustomerSettings";
import { SearchSelectInfiniteScroll } from "../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { useTranslation } from "react-i18next";

const formSchema = yup.object().shape({
  account_manager: yup.object().nullable().notRequired(),
  customer_service_rep: yup.object().nullable().notRequired(),
});

type FormData = {
  account_manager: OptionType<string> | null;
  customer_service_rep: OptionType<string> | null;
};

export const EditCustomerRelationForm = ({
  buyerTenant,
  loggedInUser,
  refreshSettingsData,
  doneEditing,
  accountManager,
  customerServiceRep,
}: {
  buyerTenant: Tenant;
  loggedInUser: User;
  refreshSettingsData: () => void;
  doneEditing: () => void;
  accountManager: User | undefined;
  customerServiceRep: User | undefined;
}) => {
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storefront_id } = useStoreState();
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleSubmit, control, errors, formState } = useFormWrapper<FormData>(
    {
      resolver: yupResolver(formSchema),
      defaultValues: {
        account_manager: accountManager
          ? convertUserToOption(accountManager)
          : null,
        customer_service_rep: customerServiceRep
          ? convertUserToOption(customerServiceRep)
          : null,
      },
    }
  );

  const onSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    try {
      const { account_manager, customer_service_rep } = formData;

      let requestBody: ITenantCustomerSettingsCreation = {};
      if (account_manager?.value) {
        requestBody.account_manager_id = account_manager.value;
      }
      if (customer_service_rep?.value) {
        requestBody.customer_service_rep_id = customer_service_rep.value;
      }

      await Axios.patch(
        endpoints.v1_storefronts_id_tenants_id_customers_id_settings(
          storefront_id,
          // Since this edit form is only used by sellers (not buyers), the
          // logged in user will belong to the seller tenant, so that's how we
          // get the seller tenant ID.
          loggedInUser.tenant_id,
          buyerTenant.id
        ),
        requestBody
      );
      notifySuccess("Changes saved");
      setIsSubmitting(false);
      refreshSettingsData();
      doneEditing();
    } catch (error) {
      notifyError("There was an error submitting the changes", { error });
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SearchSelectInfiniteScroll}
          control={control}
          name="account_manager"
          defaultValue={{ label: "", value: "" }}
          errors={errors}
          formState={formState}
          placeholder={t("Account Manager")}
          baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
            storefront_id,
            loggedInUser.tenant_id
          )}
          params={(() => {
            const params = new URLSearchParams();
            params.append("order_by", "asc");
            params.append("is_active", "true");
            return params;
          })()}
          getOptions={(response: User[]) =>
            response.map((user) => convertUserToOption(user))
          }
          rules={{
            required: true,
          }}
        />
        <Controller
          as={SearchSelectInfiniteScroll}
          control={control}
          name="customer_service_rep"
          defaultValue={{ label: "", value: "" }}
          errors={errors}
          formState={formState}
          placeholder={t("Customer Success Manager")}
          baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
            storefront_id,
            loggedInUser.tenant_id
          )}
          params={(() => {
            const params = new URLSearchParams();
            params.append("order_by", "asc");
            params.append("is_active", "true");
            params.append("id", customerServiceRep?.id ?? ""); // add distinguishing param
            return params;
          })()}
          getOptions={(response: User[]) =>
            response.map((user) => convertUserToOption(user))
          }
          rules={{
            required: true,
          }}
        />
        <ButtonContainer>
          <TertiaryButtonLargeV2 onClick={doneEditing}>
            Cancel
          </TertiaryButtonLargeV2>

          <PrimaryButtonLargeV2 loading={isSubmitting}>
            Save
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </FormContainer>
  );
};
